// @ts-nocheck
import React, { useEffect, useState, useMemo } from 'react'
import { motion, AnimatePresence } from 'framer-motion'
// lucide-react removed for reliable Vite build on this Windows setup; unicode + CSS aesthetics preserved
import { toast, Toaster } from 'sonner'
import { format } from 'date-fns'

// ---------- Local-first types (no network, pure client) ----------
type LocalUser = { id: string; handle: string }
type LocalRitual = { id: string; deposit_id: string; variant: 'witness' | 'contain' | 'embody' | 'release'; script: string; generated_at: number }
type LocalPractice = { id: string; deposit_id: string; ritual_id?: string; practiced_at: number; duration_seconds?: number }
type LocalDeposit = {
  id: string
  title?: string
  phenomenon: string
  uncertainty_labels: string[]
  known_text: string
  unknown_text: string
  evidence_need_text: string
  next_question: string
  labor_text: string
  created_at: number
  sealed_at: number
  last_touched_at: number
  rituals: LocalRitual[]
  practices: LocalPractice[]
}

// ---------- Prominent Creator Links (required by task) ----------
function CreatorLinks({ compact = false }: { compact?: boolean }) {
  return (
    <div className={`${compact ? 'text-[11px]' : 'text-sm'} text-ink-light flex items-center gap-3 flex-wrap`}>
      <span className="text-ink-muted">a private instrument by</span>
      <a href="https://www.linkedin.com/in/mindful-abhay/" target="_blank" rel="noopener noreferrer"
         className="hover:text-accent underline underline-offset-2 font-medium">Abhay</a>
      <span className="text-ink-muted">·</span>
      <a href="https://buymeacoffee.com/abhaypratapsingh" target="_blank" rel="noopener noreferrer"
         className="hover:text-accent underline underline-offset-2 font-medium">buy me a coffee</a>
    </div>
  )
}

// ---------- Stub Turnstile (test bypass, no network) ----------
function TurnstileWidget({ onToken }: { onToken: (t: string) => void }) {
  const [solved, setSolved] = useState(false)
  return (
    <div className="mt-3">
      {!solved ? (
        <button
          onClick={() => { setSolved(true); onToken('local-test-bypass-' + Date.now()) }}
          className="button text-xs px-3 py-1.5 border border-dashed"
        >
          I am present — bypass test (local only)
        </button>
      ) : (
        <div className="text-[11px] text-[#2f5d4f] flex items-center gap-1"><span className="w-3 h-3 inline-flex items-center justify-center">✓</span> Presence confirmed (test mode)</div>
      )}
      <div className="text-[10px] text-[#8a9597] mt-1">Turnstile stub for sovereign local vault. Real key in prod.</div>
    </div>
  )
}

// ---------- Client ritual generator (faithful to mental contrasting + labor) ----------
function generateMicroRituals(d: Omit<LocalDeposit, 'id' | 'rituals' | 'practices' | 'created_at' | 'sealed_at' | 'last_touched_at'> & { id?: string }): LocalRitual[] {
  const base = {
    deposit_id: d.id || 'tmp',
    generated_at: Date.now(),
  }
  const p = d.phenomenon.slice(0, 120)
  const l = d.labor_text.slice(0, 110)
  const q = d.next_question
  const k = d.known_text.slice(0, 90)
  const u = d.unknown_text.slice(0, 90)

  return [
    {
      id: 'r-w-' + Date.now(),
      variant: 'witness',
      script: `Witness\n\nSit with the weight that followed you: ${p}\n\nYou already know: ${k}\nYou do not know: ${u}\n\nThe labor you performed (and that no chart records): ${l}\n\nName it out loud once, here, now. Let the body register that it happened. Do not solve. Only witness.`,
      ...base
    },
    {
      id: 'r-c-' + (Date.now() + 1),
      variant: 'contain',
      script: `Contain\n\nThe question you chose to carry: “${q}”\n\nRight now the system is larger than one body. The fires, the air, the care capacity — these are not yours to hold alone.\n\nPlace the named labor (${l}) into a small box in your mind. Close the lid gently. Say: this was real work. This is held.\n\nBreathe. The vault keeps it so your ordinary minutes do not have to.`,
      ...base
    },
    {
      id: 'r-e-' + (Date.now() + 2),
      variant: 'embody',
      script: `Embody\n\nYou left the room carrying ${p}.\n\nWhat would the smallest next faithful action look like that still honors the question: ${q}\n\nDo one tiny thing with the body today that matches the scale of what you named. Not the whole future. One breath, one mask check, one sentence written only for yourself.\n\nThe labor you already did (${l}) counts. Let the body feel that it was enough for today.`,
      ...base
    },
    {
      id: 'r-r-' + (Date.now() + 3),
      variant: 'release',
      script: `Release (for now)\n\nYou have contrasted what is known against what remains open.\n\nThe evidence you need may arrive or it may not. The children will breathe what they will breathe.\n\nFor the next hour, set the vault down. The question walks with you; it does not need to be solved before you make dinner or answer an email.\n\nWhen the weight returns, the rituals are here. You do not have to carry it in your tissues alone.`,
      ...base
    }
  ]
}

// ---------- Main App (local-first SPA rebuild) ----------
export default function ContrastVault() {
  const [phase, setPhase] = useState<'landing' | 'unlock' | 'app'>('landing')
  const [user, setUser] = useState<LocalUser | null>(null)
  const [sessionToken, setSessionToken] = useState<string>('')

  const [view, setView] = useState<'vault' | 'hold' | 'ritual' | 'privacy'>('vault')
  const [deposits, setDeposits] = useState<LocalDeposit[]>([])

  const [loading, setLoading] = useState(false)
  const [search, setSearch] = useState('')

  // Unlock form
  const [passInput, setPassInput] = useState('')
  const [handleInput, setHandleInput] = useState('')
  const [turnstileToken, setTurnstileToken] = useState('')

  // New deposit form (the 5 key captures + labor)
  const [showNewDeposit, setShowNewDeposit] = useState(false)
  const [depForm, setDepForm] = useState({
    phenomenon: '',
    uncertainty_labels: [] as string[],
    newLabel: '',
    known_text: '',
    unknown_text: '',
    evidence_need_text: '',
    next_question: '',
    labor_text: '',
  })

  // Ritual practice state
  const [practicing, setPracticing] = useState<{ deposit: LocalDeposit; ritual: LocalRitual } | null>(null)
  const [currentStep, setCurrentStep] = useState(0)
  const [practiceTimer, setPracticeTimer] = useState(0)
  const [isTiming, setIsTiming] = useState(false)

  // Hold view
  const [holdDeposit, setHoldDeposit] = useState<LocalDeposit | null>(null)

  // Command palette
  const [cmdOpen, setCmdOpen] = useState(false)

  // Persist deposits
  function saveDeposits(next: LocalDeposit[]) {
    try { localStorage.setItem('cv_deposits', JSON.stringify(next)) } catch {}
    setDeposits(next)
  }

  function loadDeposits(): LocalDeposit[] {
    try {
      const raw = localStorage.getItem('cv_deposits')
      if (raw) return JSON.parse(raw)
    } catch {}
    return []
  }

  // Load persisted session + deposits on mount
  useEffect(() => {
    const saved = localStorage.getItem('contrast_vault_session')
    const ds = loadDeposits()
    setDeposits(ds)
    if (saved) {
      try {
        const { token, uid, handle } = JSON.parse(saved)
        setSessionToken(token)
        setUser({ id: uid, handle })
        setPhase('app')
        if (ds.length === 0) {
          seedDemoLocal(uid)
        }
      } catch {}
    }
  }, [])

  // Keyboard shortcuts
  useEffect(() => {
    function onKey(e: KeyboardEvent) {
      if (e.key === 'k' && (e.metaKey || e.ctrlKey)) { e.preventDefault(); setCmdOpen(v => !v) }
      if (e.key === 'Escape') { setCmdOpen(false); if (practicing) closeRitualPractice(false) }
      if (phase === 'app' && e.key.toLowerCase() === 'n' && (e.metaKey || e.ctrlKey)) {
        e.preventDefault(); openNewDeposit()
      }
      if (e.key === '/' && phase === 'app' && document.activeElement?.tagName !== 'INPUT' && document.activeElement?.tagName !== 'TEXTAREA') {
        e.preventDefault(); (document.querySelector('#vault-search') as HTMLInputElement)?.focus()
      }
      if (e.key.toLowerCase() === '?' && phase === 'app') { e.preventDefault(); setView('privacy') }
    }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [phase, practicing])

  // Practice timer
  useEffect(() => {
    let iv: any
    if (isTiming) {
      iv = setInterval(() => setPracticeTimer(t => t + 1), 1000)
    }
    return () => clearInterval(iv)
  }, [isTiming])

  function seedDemoLocal(uid: string) {
    const now = Date.now()
    const demo1: LocalDeposit = {
      id: 'demo-c1-' + now,
      title: 'After cardiology — the fires and the stents',
      phenomenon: 'Left the cardiologist with a new stent and the quiet knowledge that the wildfire smoke that rolled through last summer is now a recurring seasonal threat to everything I just had fixed. He was kind. He did not have language for this.',
      uncertainty_labels: ['seasonal air quality as cardiac risk', 'intergenerational bodily debt', 'local care system fragility under climate load'],
      known_text: 'I know my own scan and the stent placement were successful. I know the regional air quality alerts are real and published by the health department. I know my children will breathe more of this than I did at their age.',
      unknown_text: 'I do not know whether the next bad smoke year will be the one that undoes what the stent bought me. I do not know how many more summers the local ER and cardiology follow-up will be able to absorb the surge. I do not know what "protective" even looks like for them when the decisions are not mine.',
      evidence_need_text: 'A clear, localized projection of how many additional cardiac events per 10,000 people our region should expect under two more seasons like 2024 — and whether our current bed and specialist capacity absorbs it or breaks.',
      next_question: 'What is the smallest, least conflictual thing I can do this month that still honors that I now carry this knowledge in my body and for my children?',
      labor_text: 'I sat in the car for twenty-two minutes after the appointment before I could drive. I researched three different mask standards and cross-checked them against the actual filtration data while my partner was making dinner. I decided not to bring any of it up at the family meal because my mother-in-law was already anxious about her own lungs and I did not want to start the argument that always follows when I name the larger thing. That decision — to carry it alone for one more evening — was also labor.',
      created_at: now - 100000,
      sealed_at: now - 90000,
      last_touched_at: now - 90000,
      rituals: [],
      practices: []
    }
    demo1.rituals = generateMicroRituals(demo1)

    const demo2: LocalDeposit = {
      id: 'demo-t2-' + now,
      title: 'Therapy — "the fires are coming and I cannot protect them"',
      phenomenon: 'In session I finally said out loud the sentence I had been swallowing for months: the fires are coming and I cannot meaningfully protect my children from what is already statistically likely in their lifetimes. My therapist did not have a protocol for this. Neither did I.',
      uncertainty_labels: ['parental protection fantasy collapse', 'future unknowability vs present action', 'grief that has no ritual container in ordinary life'],
      known_text: 'I know the climate models and the regional fire projections are not fringe. I know my children are currently healthy and that ordinary parenting still matters. I know that most of what will shape their air and water is decided far above my individual choices.',
      unknown_text: 'I do not know whether the specific places we might move will actually be safer in 2040 or whether we are simply trading one set of cascading failures for another. I do not know how to talk to my children about this without either lying or handing them a burden they cannot yet carry.',
      evidence_need_text: 'An honest, non-panic-inducing map (updated yearly) of places where the combination of projected extremes + existing healthcare + community cohesion still offers a plausible ordinary childhood for people currently under 10.',
      next_question: 'How do I keep the truth of this in my body without it leaking into every ordinary moment of being their parent?',
      labor_text: 'I have spent hours at night reading papers I am not trained to read. I have quietly changed our emergency kit three times this year. I have practiced, in the mirror, the exact tone of voice I will use if we ever have to leave and I need the children to move without freezing. None of this is visible to anyone except me. Naming it here is the first time it has existed outside my own skull.',
      created_at: now - 200000,
      sealed_at: now - 190000,
      last_touched_at: now - 190000,
      rituals: [],
      practices: []
    }
    demo2.rituals = generateMicroRituals(demo2)

    const seeded = [demo2, demo1]
    saveDeposits(seeded)
    toast.success('Loaded two realistic example deposits (cardiology + therapy). Explore how the vault holds post-appointment weight.')
  }

  function performUnlock() {
    if (!passInput.trim() || !handleInput.trim()) {
      toast.error('Both a strong passphrase and a short handle are required.')
      return
    }
    if (!turnstileToken) {
      toast.error('Confirm your presence with the test bypass first.')
      return
    }
    setLoading(true)
    // Pure local "auth" — derive id from passphrase+handle (same as worker would)
    const uid = 'u-' + btoa(passInput.trim() + '::' + handleInput.trim()).replace(/[^a-z0-9]/gi, '').slice(0, 18)
    const u: LocalUser = { id: uid, handle: handleInput.trim().slice(0, 40) }
    const token = 'local-' + uid + '-' + Date.now()
    const sess = { token, uid: u.id, handle: u.handle }
    localStorage.setItem('contrast_vault_session', JSON.stringify(sess))
    setSessionToken(token)
    setUser(u)
    setPhase('app')
    setPassInput('')
    setTurnstileToken('')
    const existing = loadDeposits()
    if (existing.length === 0) {
      seedDemoLocal(u.id)
    } else {
      setDeposits(existing)
    }
    toast.success(`Vault unlocked. Welcome back, ${u.handle}.`)
    setLoading(false)
  }

  function lockWorkspace() {
    localStorage.removeItem('contrast_vault_session')
    setPhase('unlock')
    setUser(null)
    setSessionToken('')
    setDeposits([])
    setView('vault')
    setPracticing(null)
    setHoldDeposit(null)
    setShowNewDeposit(false)
    toast('Vault locked on this device. The weight stays with you only as long as you choose.')
  }

  function openNewDeposit() {
    setDepForm({
      phenomenon: '',
      uncertainty_labels: [],
      newLabel: '',
      known_text: '',
      unknown_text: '',
      evidence_need_text: '',
      next_question: '',
      labor_text: '',
    })
    setShowNewDeposit(true)
    setView('vault')
  }

  function addLabel() {
    const l = depForm.newLabel.trim()
    if (!l) return
    if (!depForm.uncertainty_labels.includes(l)) {
      setDepForm(f => ({ ...f, uncertainty_labels: [...f.uncertainty_labels, l], newLabel: '' }))
    }
  }

  function removeLabel(l: string) {
    setDepForm(f => ({ ...f, uncertainty_labels: f.uncertainty_labels.filter(x => x !== l) }))
  }

  const canSeal =
    depForm.phenomenon.trim().length > 10 &&
    depForm.uncertainty_labels.length > 0 &&
    depForm.known_text.trim().length > 5 &&
    depForm.unknown_text.trim().length > 5 &&
    depForm.evidence_need_text.trim().length > 5 &&
    depForm.next_question.trim().length > 5 &&
    depForm.labor_text.trim().length > 5

  function sealDeposit() {
    if (!user || !canSeal) return
    setLoading(true)
    const now = Date.now()
    const id = 'd-' + now.toString(36) + '-' + Math.random().toString(36).slice(2, 8)
    const base: any = {
      id,
      title: depForm.uncertainty_labels[0] || 'Untitled holding',
      phenomenon: depForm.phenomenon.trim(),
      uncertainty_labels: [...depForm.uncertainty_labels],
      known_text: depForm.known_text.trim(),
      unknown_text: depForm.unknown_text.trim(),
      evidence_need_text: depForm.evidence_need_text.trim(),
      next_question: depForm.next_question.trim(),
      labor_text: depForm.labor_text.trim(),
      created_at: now,
      sealed_at: now,
      last_touched_at: now,
    }
    const newDep: LocalDeposit = {
      ...base,
      rituals: generateMicroRituals(base),
      practices: []
    }
    const next = [newDep, ...deposits]
    saveDeposits(next)
    setShowNewDeposit(false)
    setHoldDeposit(newDep)
    setView('hold')
    toast.success('Deposit sealed into the vault. Four micro-ritual scripts generated locally from your contrast and the labor you named.')
    setLoading(false)
  }

  function openDeposit(id: string) {
    const d = deposits.find(x => x.id === id)
    if (d) {
      setHoldDeposit(d)
      setView('hold')
    }
  }

  function startRitualPractice(deposit: LocalDeposit, ritual: LocalRitual) {
    setPracticing({ deposit, ritual })
    setCurrentStep(0)
    setPracticeTimer(0)
    setIsTiming(false)
    setView('ritual')
  }

  function closeRitualPractice(recorded: boolean) {
    if (practicing && recorded && user) {
      const now = Date.now()
      const p: LocalPractice = {
        id: 'p-' + now,
        deposit_id: practicing.deposit.id,
        ritual_id: practicing.ritual.id,
        practiced_at: now,
        duration_seconds: practiceTimer > 5 ? practiceTimer : undefined
      }
      const updated = deposits.map(dep => {
        if (dep.id === practicing.deposit.id) {
          return {
            ...dep,
            practices: [...(dep.practices || []), p],
            last_touched_at: now
          }
        }
        return dep
      })
      saveDeposits(updated)
      // also update hold if open
      if (holdDeposit && holdDeposit.id === practicing.deposit.id) {
        setHoldDeposit(updated.find(x => x.id === holdDeposit.id) || null)
      }
      toast.success('Practice witnessed and logged locally. The vault has seen this labor.')
    }
    setPracticing(null)
    setCurrentStep(0)
    setPracticeTimer(0)
    setIsTiming(false)
    setView('vault')
  }

  function drawFreshRitual(depositId: string) {
    const idx = deposits.findIndex(d => d.id === depositId)
    if (idx < 0) return
    const dep = deposits[idx]
    const freshRituals = generateMicroRituals(dep)
    const updatedDep = { ...dep, rituals: [...dep.rituals, ...freshRituals], last_touched_at: Date.now() }
    const next = [...deposits]
    next[idx] = updatedDep
    saveDeposits(next)
    setHoldDeposit(updatedDep)
    setView('hold')
    toast('Fresh rituals drawn locally from the same contrast. Practice any.')
  }

  function prepareExport(deposit: LocalDeposit, ritual?: LocalRitual) {
    // Client only: self_html or clipboard text or full json
    const choice = prompt('Export:\n1) self_html (beautiful standalone file to save/print)\n2) Copy ritual or deposit text to clipboard\n3) Download full vault JSON\n\nEnter 1-3 (default 1):', '1') || '1'
    if (choice === '1' || choice === '') {
      const html = generateSelfRitualHTML(deposit, ritual)
      const blob = new Blob([html], { type: 'text/html' })
      const url = URL.createObjectURL(blob)
      const a = document.createElement('a')
      a.href = url
      a.download = `contrast-vault-${(deposit.title || 'deposit').toLowerCase().replace(/\s+/g, '-')}.html`
      document.body.appendChild(a); a.click(); a.remove()
      URL.revokeObjectURL(url)
      toast.success('Self-contained ritual card downloaded. Open anywhere, print, keep.')
      return
    }
    if (choice === '2') {
      const text = ritual ? ritual.script : `${deposit.phenomenon}\n\nKnown: ${deposit.known_text}\n\nUnknown: ${deposit.unknown_text}\n\nQuestion: ${deposit.next_question}\n\nLabor: ${deposit.labor_text}`
      navigator.clipboard.writeText(text).then(() => toast.success('Text copied to clipboard.'))
      return
    }
    if (choice === '3') {
      const archive = { exported_at: new Date().toISOString(), user: user?.handle, deposits }
      const blob = new Blob([JSON.stringify(archive, null, 2)], { type: 'application/json' })
      const url = URL.createObjectURL(blob)
      const a = document.createElement('a')
      a.href = url
      a.download = `contrast-vault-archive-${new Date().toISOString().slice(0,10)}.json`
      document.body.appendChild(a); a.click(); a.remove()
      URL.revokeObjectURL(url)
      toast.success('Full local vault archive downloaded.')
      return
    }
  }

  function requestErasure() {
    if (!user) return
    const phrase = `I accept that in five years I may regret deleting every record of how I held this weight.`
    const confirmation = prompt(`Type exactly:\n${phrase}`, '')
    if (confirmation !== phrase) { toast.error('Confirmation phrase did not match.'); return }
    setLoading(true)
    try {
      localStorage.removeItem('cv_deposits')
      localStorage.removeItem('contrast_vault_session')
      setDeposits([])
      setHoldDeposit(null)
      setPracticing(null)
      setTimeout(() => {
        setPhase('landing')
        setUser(null)
        setSessionToken('')
        toast.success('Local vault erased on this device. All deposits, rituals, and practices cleared.')
      }, 800)
    } finally { setLoading(false) }
  }

  const visibleDeposits = useMemo(() => {
    let list = [...deposits]
    if (search) {
      const s = search.toLowerCase()
      list = list.filter(d =>
        (d.title || '').toLowerCase().includes(s) ||
        d.phenomenon.toLowerCase().includes(s) ||
        d.next_question.toLowerCase().includes(s) ||
        d.labor_text.toLowerCase().includes(s) ||
        d.uncertainty_labels.some(l => l.toLowerCase().includes(s))
      )
    }
    return list.sort((a, b) => b.sealed_at - a.sealed_at)
  }, [deposits, search])

  const practiceCount = useMemo(() => deposits.reduce((sum, d) => sum + (d.practices?.length || 0), 0), [deposits])

  // ---------- RENDER ----------

  if (phase === 'landing') {
    return (
      <div className="min-h-screen bg-[#f4f1e9] text-[#1f2528]">
        <div className="max-w-3xl mx-auto px-6 pt-16 pb-24">
          <div className="flex items-start justify-between">
            <div>
              <div className="text-7xl font-semibold tracking-[-3.4px] serif text-[#1f2528]">contrast vault</div>
              <div className="mt-2 text-2xl tracking-tight text-[#5c6b6d]">a memory vault for mental contrasting after appointments</div>
            </div>
            <CreatorLinks />
          </div>

          <div className="mt-10 max-w-2xl text-[15.2px] leading-relaxed text-[#3a4648] space-y-5">
            <p>You leave the appointment carrying something the chart does not record: the knowledge that the systems your body and your people depend on are degrading, and that there is no clean, individual, low-conflict action that matches the scale of the dread.</p>
            <p>This is climate anxiety without actionable agency. It is not abstract. It follows you into the car, into the kitchen, into the night.</p>
            <p className="font-medium">Contrast Vault exists so you can confront it without being trapped by it, and without having to make the labor of confronting it visible or contestable to anyone else.</p>
          </div>

          <div className="mt-8 grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
            <div className="card p-5">
              <div className="font-medium text-[#2f5d4f]">Mental contrasting, contained</div>
              <div className="mt-2 text-[#5c6b6d]">You name the internal state you wish you could inhabit. You contrast it with the raw present. You identify the real obstacles (the unknowns, the systemic, your own limits). The energy of the gap is turned into a tiny, repeatable ritual that lives in the vault instead of in your body all day.</div>
            </div>
            <div className="card p-5">
              <div className="font-medium text-[#4a3d5a]">Invisible labor made visible — safely</div>
              <div className="mt-2 text-[#5c6b6d]">The form explicitly asks you to name the work you did to leave the room functioning (the research, the containment for family, the decision not to start the fight). That labor is witnessed by you inside the vault. The rituals generated from it never require you to explain or perform it for others.</div>
            </div>
          </div>

          <div className="mt-8 text-sm text-[#5c6b6d]">
            Every deposit requires the structured captures: uncertainty label(s), known/unknown split, evidence need, and the single next question you will carry rather than force closed. Nothing here is a calendar, a streak, or a social surface.
          </div>

          <div className="mt-10 flex flex-wrap gap-3">
            <button onClick={() => setPhase('unlock')} className="button button-primary px-8 py-3 text-base flex items-center gap-2">
              Enter the vault <span className="w-4 h-4 inline-flex items-center justify-center">→</span>
            </button>
            <button onClick={() => { setPhase('unlock'); setPassInput('demo-vault-passphrase-2026'); setHandleInput('post-appt'); }} className="button px-6 py-3 text-base border">
              Try with seeded examples
            </button>
          </div>

          <div className="mt-16 border-t border-[#d8d1c4] pt-8 text-xs text-[#8a9597] max-w-prose">
            This is not therapy. It is a tool for your own sovereignty after the room. Everything is private by default (localStorage on this device). Full self-serve deletion is always available. No accounts, no discovery, no one else can ever see what you put here unless you deliberately export a single artifact.
          </div>
          <div className="mt-6"><CreatorLinks /></div>
        </div>
      </div>
    )
  }

  if (phase === 'unlock') {
    return (
      <div className="min-h-screen bg-[#f4f1e9] flex items-center justify-center p-6">
        <div className="w-full max-w-md">
          <div className="text-center mb-8">
            <div className="text-5xl font-semibold tracking-[-2px] serif">contrast vault</div>
            <div className="text-[#5c6b6d] mt-1">Unlock your private memory vault (local)</div>
          </div>

          <div className="card p-7">
            <div className="text-xs uppercase tracking-[1px] text-[#8a9597] mb-1">Your sovereign workspace</div>
            <input className="input w-full mt-4 px-4 py-3 text-base" placeholder="Short handle (e.g. post-appt, A.P., quiet-holder)" value={handleInput} onChange={e => setHandleInput(e.target.value)} />
            <input type="password" className="input w-full mt-3 px-4 py-3 text-base font-mono" placeholder="Strong passphrase (store in password manager)" value={passInput} onChange={e => setPassInput(e.target.value)} />
            <div className="mt-2 text-[11px] text-[#8a9597]">This passphrase is never stored. It is your only key across devices for the same data. Lose it and the local vault here is inaccessible — by design.</div>

            <div className="mt-4">
              <TurnstileWidget onToken={setTurnstileToken} />
            </div>

            <button onClick={performUnlock} disabled={loading} className="button button-primary w-full mt-6 py-3 text-base">Unlock vault</button>

            <div className="mt-4 text-[11px] text-[#8a9597] leading-snug">
              Data lives in your browser localStorage. Same passphrase + handle on this device restores everything.
            </div>
          </div>

          <div className="mt-6 text-center"><CreatorLinks compact /></div>
        </div>
      </div>
    )
  }

  // MAIN VAULT SHELL (local)
  return (
    <div className="min-h-screen bg-[#f4f1e9] text-[#1f2528]">
      {/* Top bar — calm, non-gamified */}
      <div className="sticky top-0 z-50 border-b border-[#d8d1c4] bg-[#f4f1e9]/95 backdrop-blur">
        <div className="max-w-5xl mx-auto px-5 h-14 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <button onClick={() => { setView('vault'); setHoldDeposit(null); setPracticing(null); setShowNewDeposit(false) }} className="font-semibold tracking-[-0.6px] serif text-2xl hover:text-[#2f5d4f]">contrast vault</button>
            <div className="text-[10px] px-2 py-px rounded bg-[#f0ede5] text-[#5c6b6d] font-mono">{user?.handle}</div>
          </div>

          <div className="flex items-center gap-2 text-sm">
            <button onClick={() => { setView('vault'); setHoldDeposit(null); setShowNewDeposit(false) }} className={`px-3 py-1.5 rounded ${view === 'vault' ? 'bg-white border border-[#d8d1c4]' : 'hover:bg-white/60'}`}>Vault</button>
            <button onClick={() => setView('privacy')} className={`px-3 py-1.5 rounded ${view === 'privacy' ? 'bg-white border border-[#d8d1c4]' : 'hover:bg-white/60'}`}>Privacy</button>
            <button onClick={openNewDeposit} className="button button-primary px-4 py-1.5 text-sm flex items-center gap-1.5 ml-2"><span className="w-3.5 h-3.5 inline-flex items-center justify-center">+</span> New deposit</button>
            <button onClick={() => setCmdOpen(true)} className="button px-3 py-1.5 text-sm flex items-center gap-1 border"><span className="w-3.5 h-3.5 inline-flex items-center justify-center">⌘</span></button>
            <button onClick={lockWorkspace} className="button px-3 py-1.5 text-sm flex items-center gap-1 border ml-1"><span className="w-3.5 h-3.5 inline-flex items-center justify-center">🔒</span></button>
          </div>
        </div>
      </div>

      <div className="max-w-5xl mx-auto px-5 pt-8 pb-24">
        {/* VAULT HOME — list of sealed deposits */}
        {view === 'vault' && !showNewDeposit && !holdDeposit && (
          <>
            <div className="flex items-end justify-between mb-6">
              <div>
                <div className="uppercase text-[10px] tracking-[1.5px] text-[#8a9597]">Your memory vault (local)</div>
                <div className="text-3xl tracking-tight">What you have held</div>
              </div>
              <div className="flex gap-2">
                <div className="relative">
                  <input id="vault-search" value={search} onChange={e => setSearch(e.target.value)} placeholder="Search deposits..." className="input pl-9 w-72 py-2 text-sm" />
                  <span className="w-4 h-4 absolute left-3 top-3 text-[#8a9597] inline-flex items-center justify-center">🔎</span>
                </div>
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {visibleDeposits.length === 0 && <div className="text-[#5c6b6d] col-span-full py-10 text-center border border-dashed rounded">No deposits yet. After an appointment, make a new deposit. The vault will hold it privately on this device.</div>}
              {visibleDeposits.map(d => (
                <div key={d.id} onClick={() => openDeposit(d.id)} className="vault-card p-5 cursor-pointer hover:border-[#8a6f47] group">
                  <div className="flex items-center gap-2">
                    <div className="font-medium tracking-tight leading-tight group-hover:text-[#2f5d4f]">{d.title || d.uncertainty_labels?.slice(0,60).join(', ') || 'Held contrast'}</div>
                    <div className="seal-stamp ml-auto">SEALED</div>
                  </div>
                  <div className="text-sm text-[#5c6b6d] mt-3 line-clamp-3">{d.phenomenon}</div>
                  <div className="mt-4 text-xs text-[#8a9597] flex items-center gap-2">
                    <span>{format(new Date(d.sealed_at), 'MMM d, yyyy')}</span>
                    <span className="pill">{d.uncertainty_labels.length} uncertainties</span>
                    <span className="pill">{d.rituals.length} rituals</span>
                    <span className="pill">{d.practices?.length || 0} practiced</span>
                  </div>
                  <div className="mt-2 text-xs text-[#4a3d5a] italic line-clamp-1">“{d.next_question}”</div>
                </div>
              ))}
            </div>

            <div className="mt-12 text-xs text-[#8a9597] max-w-prose">
              Pure local vault. Open any deposit to sit with the full contrast and practice its micro-rituals. Search works instantly. Exports are instant client-side files.
            </div>
          </>
        )}

        {/* NEW DEPOSIT — the cognitively demanding form (5 captures) */}
        {showNewDeposit && (
          <div className="max-w-3xl">
            <button onClick={() => setShowNewDeposit(false)} className="text-xs text-[#5c6b6d] hover:underline mb-4">← back to vault</button>

            <div className="uppercase text-[10px] tracking-[1.5px] text-[#8a9597]">New deposit into the vault</div>
            <div className="text-3xl tracking-tight mt-1">After the appointment</div>
            <div className="text-sm text-[#5c6b6d] mt-2">Take your time. The form will not let you seal until the contrast and the labor are named. This is deliberate.</div>

            <div className="mt-8">
              <div className="field-label mb-1.5">What the appointment left with you (the weight that followed you out)</div>
              <textarea className="textarea w-full p-4 text-[15px]" placeholder="I left the room carrying..." value={depForm.phenomenon} onChange={e => setDepForm(f => ({...f, phenomenon: e.target.value}))} />
            </div>

            <div className="mt-8">
              <div className="field-label mb-1.5">Uncertainty label(s) — name the contours of not-knowing</div>
              <div className="flex flex-wrap gap-2 mb-2">
                {depForm.uncertainty_labels.map(l => (
                  <span key={l} className="pill flex items-center gap-1 pr-1">{l} <button onClick={() => removeLabel(l)} className="text-[#8a9597] hover:text-red-600">×</button></span>
                ))}
              </div>
              <div className="flex gap-2">
                <input className="input flex-1" placeholder="Type an uncertainty and press add" value={depForm.newLabel} onChange={e => setDepForm(f => ({...f, newLabel: e.target.value}))} onKeyDown={e => { if (e.key === 'Enter') addLabel() }} />
                <button onClick={addLabel} className="button">Add</button>
              </div>
              <div className="text-[11px] text-[#8a9597] mt-1">Examples: seasonal air as cardiac risk, parental protection fantasy collapse, local care system fragility under climate load.</div>
            </div>

            {/* Known / Unknown split — the heart of mental contrasting */}
            <div className="mt-8 grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="split-col known">
                <div className="text-xs font-medium mb-1.5 text-[#2f5d4f]">KNOWN — what I can stand on</div>
                <textarea className="textarea w-full h-40 text-sm" placeholder="Facts, evidence, or realities I am not pretending away..." value={depForm.known_text} onChange={e => setDepForm(f => ({...f, known_text: e.target.value}))} />
              </div>
              <div className="split-col unknown">
                <div className="text-xs font-medium mb-1.5 text-[#4a3d5a]">UNKNOWN — what remains opaque or uncontrollable</div>
                <textarea className="textarea w-full h-40 text-sm" placeholder="Futures, systems, limits, and unknowables I cannot resolve today..." value={depForm.unknown_text} onChange={e => setDepForm(f => ({...f, unknown_text: e.target.value}))} />
              </div>
            </div>

            <div className="mt-8">
              <div className="field-label mb-1.5">Evidence need — what would meaningfully update the weight?</div>
              <textarea className="textarea w-full p-4 text-sm" placeholder="I would need X (specific, not vague) to feel the ground shift under this..." value={depForm.evidence_need_text} onChange={e => setDepForm(f => ({...f, evidence_need_text: e.target.value}))} />
            </div>

            <div className="mt-8">
              <div className="field-label mb-1.5">The next question — the one I will carry rather than force closed</div>
              <textarea className="textarea w-full p-4 text-sm" placeholder="The single question I am willing to let walk with me..." value={depForm.next_question} onChange={e => setDepForm(f => ({...f, next_question: e.target.value}))} />
            </div>

            {/* Invisible labor — the resolution to the design constraint */}
            <div className="mt-8 labor-box card p-5">
              <div className="flex items-center gap-2 text-sm font-medium text-[#8a6f47]"><span className="w-4 h-4 inline-flex items-center justify-center">🔥</span> The invisible labor I performed today</div>
              <div className="text-xs text-[#8a9597] mt-1">This is the work that usually stays off every record: the metabolizing, the research at 1 a.m., the decision not to start the fight at dinner, the quiet changes to the emergency kit, the containment for people who need you to be steady. Name it so it is no longer invisible to you.</div>
              <textarea className="textarea w-full mt-3 h-28 p-4 text-sm" placeholder="I carried..." value={depForm.labor_text} onChange={e => setDepForm(f => ({...f, labor_text: e.target.value}))} />
            </div>

            <div className="mt-8 flex gap-3">
              <button onClick={sealDeposit} disabled={!canSeal || loading} className="button button-primary px-8 py-2.5 text-base flex-1">Seal this deposit into the vault</button>
              <button onClick={() => setShowNewDeposit(false)} className="button px-6 py-2.5 text-base flex-1">Cancel</button>
            </div>
            <div className="text-[11px] text-[#8a9597] mt-3">Once sealed, four micro-ritual scripts are generated instantly on this device from the contrast and the labor you named. Practice them here. Everything stays private.</div>
          </div>
        )}

        {/* HOLD — calm, beautiful, read-only presentation of a full deposit + its rituals */}
        {holdDeposit && !showNewDeposit && !practicing && (
          <div className="max-w-3xl mx-auto zen pt-2">
            <button onClick={() => { setHoldDeposit(null); setView('vault') }} className="text-xs underline mb-6">← back to vault</button>

            <div className="hold-title serif mb-2">{holdDeposit.title || 'Held contrast'}</div>
            <div className="text-xs text-[#8a9597] mb-6">Sealed {format(new Date(holdDeposit.sealed_at), 'MMMM d, yyyy')} • {holdDeposit.practices?.length || 0} practices recorded</div>

            <div className="section">
              <div className="uppercase text-xs tracking-widest text-[#2f5d4f] mb-2">What followed me out of the room</div>
              <div>{holdDeposit.phenomenon}</div>
            </div>

            <div className="section grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <div className="uppercase text-xs tracking-widest text-[#2f5d4f] mb-2">Known</div>
                <div className="text-[15px]">{holdDeposit.known_text}</div>
              </div>
              <div>
                <div className="uppercase text-xs tracking-widest text-[#4a3d5a] mb-2">Unknown</div>
                <div className="text-[15px]">{holdDeposit.unknown_text}</div>
              </div>
            </div>

            <div className="section">
              <div className="uppercase text-xs tracking-widest text-[#8a9597] mb-2">Evidence that would update this</div>
              <div>{holdDeposit.evidence_need_text}</div>
            </div>

            <div className="section">
              <div className="uppercase text-xs tracking-widest text-[#3d4a5a] mb-2">The question I carry</div>
              <div className="text-[15px] italic">“{holdDeposit.next_question}”</div>
            </div>

            <div className="section labor-box p-5 rounded">
              <div className="uppercase text-xs tracking-widest text-[#8a6f47] mb-2">The labor I named (witnessed here)</div>
              <div>{holdDeposit.labor_text}</div>
            </div>

            <div className="mt-8">
              <div className="uppercase text-xs tracking-widest text-[#8a9597] mb-3">Micro-rituals drawn from this contrast (local)</div>
              {holdDeposit.rituals.length === 0 && (
                <div className="text-sm text-[#5c6b6d]">No rituals yet — seal again or draw fresh.</div>
              )}
              {holdDeposit.rituals.map(r => (
                <div key={r.id} className="card p-4 mb-3">
                  <div className="text-xs uppercase tracking-widest text-[#8a6f47] mb-1">{r.variant}</div>
                  <div className="whitespace-pre-wrap text-[15px] leading-relaxed">{r.script}</div>
                  <div className="mt-4 flex gap-2">
                    <button onClick={() => startRitualPractice(holdDeposit, r)} className="button button-primary px-5 py-1.5 text-sm flex items-center gap-2"><span className="w-3.5 h-3.5 inline-flex items-center justify-center">🪶</span> Practice this ritual</button>
                    <button onClick={() => prepareExport(holdDeposit, r)} className="button px-4 py-1.5 text-sm flex items-center gap-2"><span className="w-3.5 h-3.5 inline-flex items-center justify-center">⬇</span> Export</button>
                  </div>
                </div>
              ))}
            </div>

            <div className="mt-6 flex gap-3">
              <button onClick={() => drawFreshRitual(holdDeposit.id)} className="button px-5 py-2 text-sm flex items-center gap-2"><span className="w-3.5 h-3.5 inline-flex items-center justify-center">⟳</span> Draw fresh rituals locally</button>
              <button onClick={() => prepareExport(holdDeposit)} className="button px-5 py-2 text-sm flex items-center gap-2"><span className="w-3.5 h-3.5 inline-flex items-center justify-center">⬇</span> Export this holding</button>
            </div>

            <div className="text-xs text-[#8a9597] mt-10 border-t pt-6">This view exists so you can simply sit with the structure of what you are carrying. No editing. No resolution demanded. When you are ready, close the vault. All data is local to this browser.</div>
          </div>
        )}

        {/* RITUAL PRACTICE — demanding, slow, contained attention */}
        {practicing && view === 'ritual' && (
          <div className="max-w-2xl mx-auto pt-6">
            <div className="text-xs uppercase tracking-[1.5px] text-[#8a9597]">Micro-ritual practice — {practicing.ritual.variant}</div>
            <div className="text-2xl tracking-tight mt-1 mb-6">Take this slowly. It is the whole point.</div>

            {practicing.ritual.script.split('\n').filter(Boolean).map((step, idx) => (
              <div key={idx} className={`ritual-step ${idx === currentStep ? 'active' : ''}`}>
                <div className="text-sm text-[#8a9597] mb-1">Step {idx + 1}</div>
                <div className="text-[15.5px] leading-relaxed">{step}</div>
              </div>
            ))}

            <div className="mt-6 flex items-center gap-3">
              <button onClick={() => setCurrentStep(Math.max(0, currentStep - 1))} className="button">Previous</button>
              <button onClick={() => setCurrentStep(Math.min(practicing.ritual.script.split('\n').filter(Boolean).length - 1, currentStep + 1))} className="button">Next</button>

              <button onClick={() => setIsTiming(!isTiming)} className="button border ml-auto flex items-center gap-2">
                <span className="w-3.5 h-3.5 inline-flex items-center justify-center">⏱</span> {isTiming ? 'Pause timer' : 'Start gentle timer'}
              </button>
              <div className="tabular-nums text-sm text-[#8a9597] w-12 text-right">{practiceTimer}s</div>
            </div>

            <div className="mt-8 flex gap-3">
              <button onClick={() => closeRitualPractice(true)} className="button button-primary flex-1 py-3">I practiced this. Witness it.</button>
              <button onClick={() => closeRitualPractice(false)} className="button flex-1 py-3">Close without recording</button>
            </div>

            <div className="text-xs text-[#8a9597] mt-6 text-center">When you are done, the vault will close. The labor is witnessed locally. The question walks with you. Return when the weight returns.</div>
          </div>
        )}

        {/* PRIVACY & SOVEREIGNTY CENTER (local only) */}
        {view === 'privacy' && user && (
          <div className="max-w-2xl">
            <div className="text-2xl tracking-tight mb-6">Privacy &amp; sovereignty center (local)</div>

            <div className="card p-6">
              <div className="text-sm font-medium">Your vault key</div>
              <div className="text-xs text-[#5c6b6d] mt-1">Your handle is <span className="font-medium">{user.handle}</span>. The passphrase you used is the only way to access the same data (localStorage) on this browser. Never share it.</div>
              <button onClick={lockWorkspace} className="mt-4 button px-4 py-1.5 text-sm">Lock vault on this device</button>
            </div>

            <div className="card p-6 mt-4">
              <div className="font-medium">Full data archive (local)</div>
              <div className="text-sm text-[#5c6b6d] mt-1">Download a complete JSON of every deposit, generated ritual, and recorded practice. Stays on your machine.</div>
              <button onClick={() => prepareExport({} as any /* triggers full */)} className="mt-3 button px-4 py-1.5 text-sm flex items-center gap-2"><span className="w-3.5 h-3.5 inline-flex items-center justify-center">⬇</span> Download my full archive</button>
            </div>

            <div className="card p-6 mt-4 border-l-4 border-[#8f3d3d]">
              <div className="font-medium text-[#8f3d3d]">Complete local erasure</div>
              <div className="text-sm mt-2">Permanently delete every deposit, ritual, and practice record from localStorage on this browser. Irreversible here. (You can always re-seal new ones.)</div>
              <button onClick={requestErasure} className="mt-4 text-sm px-4 py-1.5 border border-[#8f3d3d] text-[#8f3d3d] rounded flex items-center gap-2 hover:bg-[#8f3d3d] hover:text-white"><span className="w-3.5 h-3.5 inline-flex items-center justify-center">🗑</span> Erase everything on this device</button>
            </div>

            <div className="mt-8 text-xs text-[#8a9597]">
              {deposits.length} deposits • {practiceCount} practices recorded locally. This vault was built so the work you do after the appointment becomes visible to you without requiring you to make it visible (and therefore contestable) to anyone else. Your data is yours.
            </div>
            <div className="mt-3"><CreatorLinks compact /></div>
          </div>
        )}
      </div>

      {/* Command palette */}
      <AnimatePresence>
        {cmdOpen && (
          <div className="cmdk" onClick={() => setCmdOpen(false)}>
            <div className="cmdk-inner" onClick={e => e.stopPropagation()}>
              <div className="p-3 text-sm border-b">Command palette (local)</div>
              <div className="p-2 text-sm">
                {[
                  { label: 'New deposit', action: () => { setCmdOpen(false); openNewDeposit() } },
                  { label: 'Go to Privacy center', action: () => { setCmdOpen(false); setView('privacy') } },
                  { label: 'Lock vault', action: () => { setCmdOpen(false); lockWorkspace() } },
                ].map((c, i) => (
                  <div key={i} onClick={c.action} className="px-4 py-2 hover:bg-[#f8f5f0] cursor-pointer rounded flex justify-between items-center">{c.label}<span className="w-3.5 h-3.5 text-[#8a9597] inline-flex items-center justify-center">→</span></div>
                ))}
              </div>
              <div className="p-3 text-[10px] text-[#8a9597] border-t">⌘K • n for new • / search • ? privacy • esc close</div>
            </div>
          </div>
        )}
      </AnimatePresence>

      <Toaster position="top-center" richColors closeButton />
    </div>
  )

  // Self-contained beautiful ritual card (client-side, zero runtime deps)
  function generateSelfRitualHTML(deposit: LocalDeposit, ritual?: LocalRitual) {
    const date = new Date().toLocaleDateString()
    const script = ritual ? ritual.script : `${deposit.next_question}\n\n${deposit.labor_text}`
    return `<!doctype html>
<html><head><meta charset="utf-8"><title>Contrast Vault — ${deposit.title || 'Held'}</title>
<style>body{font-family:system-ui,-apple-system,Georgia,serif;margin:40px auto;max-width:680px;line-height:1.7;color:#1f2528;background:#f4f1e9;padding:24px;font-size:15px}.meta{font-size:11px;color:#8a9597;letter-spacing:1px} h1{font-size:24px;letter-spacing:-0.6px;margin-bottom:4px} .core{border-left:4px solid #8a6f47;padding-left:16px;margin:24px 0;color:#3a4648} .step{margin:14px 0;padding-left:4px} .seal{font-family:Georgia,serif;font-size:10px;letter-spacing:2px;color:#8a6f47;border:1px solid #c9b8a0;padding:0 8px;border-radius:2px;display:inline-block} .labor{margin-top:28px;padding-top:18px;border-top:1px solid #d8d1c4;font-size:14px;color:#5c6b6d}</style>
</head><body>
<div class="meta">CONTRAST VAULT • PRIVATE • LOCAL • ${date} <span class="seal">SEALED</span></div>
<h1>${deposit.title || 'Held contrast'}</h1>
<div class="core">${deposit.phenomenon}</div>
<div><strong>Known</strong><br>${deposit.known_text}</div>
<div style="margin-top:16px"><strong>Unknown</strong><br>${deposit.unknown_text}</div>
<div style="margin-top:16px"><strong>The question I carry</strong><br><em>${deposit.next_question}</em></div>
<div class="labor"><strong>The labor I named</strong><br>${deposit.labor_text}</div>
${ritual ? `<div style="margin-top:32px"><div class="seal">${ritual.variant.toUpperCase()} RITUAL</div><div style="margin-top:12px;white-space:pre-wrap">${script}</div></div>` : ''}
<div style="margin-top:48px;font-size:11px;color:#8a9597">This is a deliberate, private local export from a sovereign memory vault. Nothing here was posted or discovered. The micro-ritual is for the person who sealed it.</div>
</body></html>`
  }
}
